import { useEffect, useState } from "react"
import { Box, HStack, IconButton, Separator, Spinner, Stack } from "@chakra-ui/react"
import { LuRefreshCw } from "react-icons/lu"
import { Button, Section, Text } from "@/components"
import { useDropboxDocuments } from "@/hooks"
import { ALL_STEPS, BC_FORM_STEP_INDEX, COMPLETE_NDA_FORM_STEP_INDEX, SIGN_BC_STEP_INDEX, SIGN_NDA_STEP_INDEX } from "@/constants"
import { ComplianceType, Deal } from "@/types"
import {
  BackgroundCheckFormComponent,
  CompletedTaskComponent,
  ComplianceTab,
  NdaFormComponent,
  PendingDocumentComponent,
} from "./components"
import { isCompleted } from "./utils"

interface Props {
  isLoading: boolean
  deals: Deal[]
  onRefresh?: () => void
  onNavigateToDocuments?: () => void
  currentDeal: Deal | null
  onDealChange: (deal: Deal) => void
}

export const ComplianceComponent = ({ deals, isLoading, onRefresh, onNavigateToDocuments, currentDeal, onDealChange }: Props) => {
  const [complianceTypes, setComplianceTypes] = useState<ComplianceType[]>([ComplianceType.NDA, ComplianceType.BACKGROUND_CHECK])
  const [currentComplianceType, setCurrentComplianceType] = useState<ComplianceType | null>(ComplianceType.BACKGROUND_CHECK)
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(COMPLETE_NDA_FORM_STEP_INDEX)

  const isDealInactive = currentDeal?.isActive === 0

  const { data: dropboxDocuments, isLoading: isLoadingDocs } = useDropboxDocuments(currentDeal?.id ?? undefined)

  useEffect(() => {
    if (currentDeal) {
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.currentStep);
      if (index < BC_FORM_STEP_INDEX) {
        setComplianceTypes([ComplianceType.NDA]);
        setCurrentComplianceType(ComplianceType.NDA);
        setCurrentStepIndex(index)
        return
      } else {
        setCurrentStepIndex(index)
      }

      setComplianceTypes([ComplianceType.NDA, ComplianceType.BACKGROUND_CHECK])
    }
  }, [currentDeal])

  const isNdaFormCompleted = currentStepIndex >= SIGN_NDA_STEP_INDEX

  const complianceFormComponent = () => {
    if (!currentComplianceType) return null
    if (!currentDeal) return null

    if (currentComplianceType === ComplianceType.NDA && isCompleted(currentComplianceType, dropboxDocuments || [])) {
      return <CompletedTaskComponent complianceType={currentComplianceType} onNavigateToDocuments={onNavigateToDocuments} />
    }

    switch (currentComplianceType) {
      case ComplianceType.BACKGROUND_CHECK:
        if (currentStepIndex > SIGN_BC_STEP_INDEX) {
          return <CompletedTaskComponent complianceType={ComplianceType.BACKGROUND_CHECK} onNavigateToDocuments={onNavigateToDocuments} />
        }

        return <BackgroundCheckFormComponent currentDeal={currentDeal} />
      case ComplianceType.NDA:
        if (currentStepIndex >= SIGN_NDA_STEP_INDEX) {
          return <PendingDocumentComponent complianceType={ComplianceType.NDA} showWarning onNavigateToDocuments={onNavigateToDocuments} />
        } else {
          return <NdaFormComponent isInitialScreen={false} />
        }
    }
  }

  return (
    <Section>
      <HStack>
        <Text fontSize="2xl" fontWeight="bold" fontFamily="body">
          Compliance
        </Text>
        {onRefresh && <IconButton aria-label="Refresh" variant="ghost" size="sm" onClick={onRefresh}><LuRefreshCw /></IconButton>}
      </HStack>
      {
        isLoading ? (
          <HStack w="100%" justifyContent="center" alignItems="center" p={10}>
            <Spinner />
          </HStack>
        ) : (
          <>
            <HStack mt={5} gap={2}>
              {
                deals && deals.length === 1 ? (
                  <HStack>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600" borderWidth={1} borderColor="gray.200" borderRadius="md" px={4} py={2}>
                      {deals[0].company.name}
                    </Text>
                    {deals[0].isActive === 0 && (
                      <Box px={2} py={0.5} borderRadius="full" bg="gray.100">
                        <Text fontSize="xs" fontWeight="semibold" color="gray.500">Inactive</Text>
                      </Box>
                    )}
                  </HStack>
                ) : (
                  deals && deals.map((deal, index) => (
                    <HStack key={index} gap={1}>
                      <Button
                        variant={ deal.id === currentDeal?.id ? "solid" : "outline"}
                        onClick={() => onDealChange(deal)}
                        opacity={deal.isActive === 0 ? 0.6 : 1}
                      >
                        {deal.company.name}
                      </Button>
                      {deal.isActive === 0 && (
                        <Box px={2} py={0.5} borderRadius="full" bg="gray.100">
                          <Text fontSize="xs" fontWeight="semibold" color="gray.500">Inactive</Text>
                        </Box>
                      )}
                    </HStack>
                  ))
                )
              }
            </HStack>
            {isDealInactive ? (
              <Box bg="gray.100" border="1px solid" borderColor="gray.300" borderRadius="md" px={4} py={3} mt={5}>
                <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                  You do not have an active deal in this company, please reach out to your Equity Partner if you have any questions.
                </Text>
              </Box>
            ) : (
              <>
                <HStack mt={10} flexWrap="wrap">
                  {
                    complianceTypes.map((type, index) => (
                      <ComplianceTab
                        key={index}
                        isLoading={isLoadingDocs}
                        type={type}
                        currentComplianceType={currentComplianceType}
                        complianceDocuments={dropboxDocuments}
                        setCurrentComplianceType={setCurrentComplianceType}
                        isNdaFormCompleted={type === ComplianceType.NDA ? isNdaFormCompleted : undefined}
                      />
                    ))
                  }
                </HStack>
                <Separator mt={0} />
                <Stack>
                  {
                    complianceFormComponent()
                  }
                </Stack>
              </>
            )}
          </>
        )
      }
    </Section>
  )
}
